import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, Rocket, ShieldCheck, Send, QrCode, Aperture,
  Layers, Zap, IndianRupee, Sparkles,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import BrandMark from "@/components/BrandMark";

const ANTON = '"Anton", "Impact", sans-serif';
const GOLD = "#F5A800";
const INK = "#0A0A0A";
const CREAM = "#F7F4EC";

/**
 * ForBusinessPage — dedicated marketing landing for prospective vendors.
 *
 * Replaces the previous #for-business anchor section on MarketingPage.
 * Same brand tokens, same voice, but focused entirely on business
 * onboarding: what CityDooh is, the three game types (with short
 * explanations), and the direct path to /vendor/register (which is
 * the existing self-service Vendor flow — this page does NOT introduce
 * a new signup form).
 */
export default function ForBusinessPage() {
  return (
    <div className="min-h-screen bg-[color:var(--paper,#F7F4EC)] text-[#0A0A0A]" data-testid="for-business-page">
      {/* Header — matches the rest of the marketing surfaces. */}
      <header className="border-b-2 border-[#0A0A0A] bg-[#F7F4EC]">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-[72px] md:h-[88px] lg:h-24 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 shrink-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[#0A0A0A] rounded-md" aria-label="City DOOH home" data-testid="fb-brand-home">
            <BrandMark size="header" />
          </Link>
          <nav className="flex items-center gap-4 text-xs font-bold uppercase tracking-widest">
            <Link to="/explore" className="hover:text-[#F5A800]" data-testid="fb-link-explore">Explore</Link>
            <Link to="/vendor/login" className="hover:text-[#F5A800]" data-testid="fb-link-vendor-login">Vendor login</Link>
            <Link to="/vendor/register" data-testid="fb-header-cta">
              <Button
                className="h-9 bg-[#F5A800] text-[#0A0A0A] hover:bg-[#0A0A0A] hover:text-[#F5A800] border-2 border-[#0A0A0A] font-black uppercase tracking-widest text-xs"
                style={{ fontFamily: ANTON }}
              >
                Get Started <ArrowRight className="h-3.5 w-3.5 ml-1.5" />
              </Button>
            </Link>
          </nav>
        </div>
      </header>

      {/* ─────────────── HERO ─────────────── */}
      <section className="border-b-2 border-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-16 md:py-24 grid md:grid-cols-2 gap-10 md:gap-14 items-center">
          <div>
            <span
              className="inline-flex items-center gap-1.5 text-[10px] font-black uppercase tracking-[0.25em] px-3 py-1.5 rounded-full bg-[#0A0A0A] text-[#F5A800] border-2 border-[#0A0A0A]"
              data-testid="fb-eyebrow"
            >
              <Rocket className="h-3 w-3" /> For businesses
            </span>
            <h1
              className="mt-5 text-4xl sm:text-5xl md:text-6xl leading-[0.9] uppercase tracking-tight"
              style={{ fontFamily: ANTON }}
              data-testid="fb-hero-title"
            >
              Turn your shopfront into a<br />
              <span className="text-[#F5A800]">lead-generating game.</span>
            </h1>
            <p className="mt-5 text-base md:text-lg text-[#0A0A0A]/75 max-w-xl">
              CityDooh is India&apos;s gamified marketing platform for local businesses.
              Launch a spin wheel, scratch card, or instant offer in minutes, collect verified leads,
              hand out server-decided prizes, and retarget by email and WhatsApp — no dev work required.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Link to="/vendor/register" data-testid="fb-hero-cta">
                <Button
                  className="h-12 px-6 text-sm font-black uppercase tracking-widest bg-[#F5A800] text-[#0A0A0A] border-2 border-[#0A0A0A] hover:bg-[#0A0A0A] hover:text-[#F5A800]"
                  style={{ fontFamily: ANTON }}
                >
                  Start free trial <ArrowRight className="h-4 w-4 ml-1.5" />
                </Button>
              </Link>
              <Link to="/vendor/login" data-testid="fb-hero-login">
                <Button
                  variant="outline"
                  className="h-12 px-6 text-sm font-black uppercase tracking-widest bg-transparent text-[#0A0A0A] border-2 border-[#0A0A0A] hover:bg-[#0A0A0A] hover:text-white"
                  style={{ fontFamily: ANTON }}
                >
                  Vendor login
                </Button>
              </Link>
            </div>
            <div className="mt-8 flex flex-wrap items-center gap-4 text-xs text-[#0A0A0A]/60">
              <span className="inline-flex items-center gap-1.5"><ShieldCheck className="h-3.5 w-3.5 text-[#F5A800]" /> 15-day free trial</span>
              <span className="inline-flex items-center gap-1.5"><ShieldCheck className="h-3.5 w-3.5 text-[#F5A800]" /> No card required</span>
              <span className="inline-flex items-center gap-1.5"><ShieldCheck className="h-3.5 w-3.5 text-[#F5A800]" /> Cancel anytime</span>
            </div>
          </div>
          <div className="hidden md:block relative">
            <div
              className="rounded-2xl border-2 border-[#0A0A0A] bg-white p-6 relative overflow-hidden"
              style={{ boxShadow: "8px 8px 0 #0A0A0A" }}
            >
              <div className="grid grid-cols-2 gap-4">
                <StatTile label="Lead capture rate" value="43%" />
                <StatTile label="Return-visit lift" value="2.4×" />
                <StatTile label="Setup time" value="< 5 min" />
                <StatTile label="Cost per lead" value="₹1.66" />
              </div>
              <div className="mt-5 border-t-2 border-dashed border-[#0A0A0A]/25 pt-4">
                <p className="text-xs uppercase tracking-widest text-[#0A0A0A]/60">Now live in</p>
                <p className="mt-1 text-lg font-black" style={{ fontFamily: ANTON }}>150+ Indian cities · 17 categories</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─────────────── GAME TYPES ─────────────── */}
      <section id="game-types" className="border-b-2 border-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-16 md:py-20">
          <span
            className="inline-flex items-center gap-1.5 text-[10px] font-black uppercase tracking-[0.25em] px-3 py-1.5 rounded-full bg-[#F5A800] text-[#0A0A0A] border-2 border-[#F5A800]"
          >
            <Sparkles className="h-3 w-3" /> Three game types
          </span>
          <h2
            className="mt-4 text-3xl sm:text-4xl md:text-5xl uppercase leading-[0.95] tracking-tight max-w-3xl"
            style={{ fontFamily: ANTON }}
          >
            Pick the campaign shape that fits your customer&apos;s moment.
          </h2>
          <p className="mt-3 text-[#0A0A0A]/70 max-w-2xl">
            Every game type runs on the same underlying platform — same lead capture, same coupon
            engine, same analytics. What changes is the experience your customer plays with.
          </p>

          <div className="mt-10 grid md:grid-cols-3 gap-5 md:gap-6">
            <GameTypeCard
              icon={<Aperture className="h-5 w-5" />}
              label="Spin Wheel"
              testid="fb-game-spin"
              tagline="High-drama, animated, made for footfall + storefront QRs."
              desc="A weighted wheel with your prizes. The customer spins, the server decides the outcome, the coupon issues instantly. Best when you want a moment — an event, a launch, or in-store queues."
            />
            <GameTypeCard
              icon={<Layers className="h-5 w-5" />}
              label="Scratch Card"
              testid="fb-game-scratch"
              tagline="Tactile, mobile-first, perfect for SMS/WhatsApp deep-links."
              desc="A scratchable card that reveals the prize on interaction. Same server-side prize logic as the wheel, but a quieter UX — ideal for retargeting emails or one-tap campaigns from social ads."
            />
            <GameTypeCard
              icon={<Zap className="h-5 w-5" />}
              label="Static Offer"
              testid="fb-game-static"
              tagline="No game — just a flat, claimable offer everyone gets."
              desc="Pick the offer, set the redemption terms, publish. The customer taps to claim, gets a coupon code + your redeem address on their phone. Best for straight-up discount broadcasts."
            />
          </div>

          <div className="mt-10 rounded-2xl border-2 border-[#0A0A0A] bg-white p-6" style={{ boxShadow: "6px 6px 0 #0A0A0A" }}>
            <h3 className="text-xl uppercase" style={{ fontFamily: ANTON }}>What&apos;s the same across all three</h3>
            <ul className="mt-3 grid sm:grid-cols-2 gap-x-6 gap-y-2 text-sm text-[#0A0A0A]/80">
              <FeatureLine>Server-decided prizes (no client-side randomness)</FeatureLine>
              <FeatureLine>Unique cryptographic coupon codes</FeatureLine>
              <FeatureLine>Verified lead capture (name, phone, email)</FeatureLine>
              <FeatureLine>WhatsApp + email retargeting</FeatureLine>
              <FeatureLine>QR + shareable link out of the box</FeatureLine>
              <FeatureLine>Real-time analytics dashboard</FeatureLine>
            </ul>
          </div>
        </div>
      </section>

      {/* ─────────────── HOW IT WORKS ─────────────── */}
      <section className="border-b-2 border-[#0A0A0A] bg-[#0A0A0A] text-white">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-16 md:py-20">
          <span
            className="inline-flex items-center gap-1.5 text-[10px] font-black uppercase tracking-[0.25em] px-3 py-1.5 rounded-full bg-[#F5A800] text-[#0A0A0A] border-2 border-[#F5A800]"
          >
            How it works
          </span>
          <h2
            className="mt-4 text-3xl sm:text-4xl md:text-5xl uppercase leading-[0.95] tracking-tight max-w-3xl"
            style={{ fontFamily: ANTON }}
          >
            Live in under 5 minutes.
          </h2>
          <div className="mt-10 grid md:grid-cols-3 gap-5">
            <Step step="1" icon={<Rocket className="h-5 w-5" />} title="Build your campaign" desc="Pick a game type, set prizes, set win probabilities. Live preview as you build." />
            <Step step="2" icon={<QrCode className="h-5 w-5" />} title="Share QR or link" desc="Every campaign ships with a downloadable QR + shareable URL for storefront, flyers, socials." />
            <Step step="3" icon={<Send className="h-5 w-5" />} title="Retarget & convert" desc="Verified leads become an audience. Bring them back with email + WhatsApp reminders." />
          </div>
        </div>
      </section>

      {/* ─────────────── PRICING ─────────────── */}
      <section className="border-b-2 border-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-16 md:py-20">
          <span
            className="inline-flex items-center gap-1.5 text-[10px] font-black uppercase tracking-[0.25em] px-3 py-1.5 rounded-full bg-[#0A0A0A] text-[#F5A800] border-2 border-[#0A0A0A]"
          >
            <IndianRupee className="h-3 w-3" /> Simple pricing
          </span>
          <h2
            className="mt-4 text-3xl sm:text-4xl md:text-5xl uppercase leading-[0.95] tracking-tight max-w-3xl"
            style={{ fontFamily: ANTON }}
          >
            Pay only for what works.
          </h2>
          <div className="mt-8 grid md:grid-cols-3 gap-4">
            <Price title="Free Trial" amount="Free" suffix="15 days" bullets={["Up to 300 leads", "Full customization", "No card required"]} />
            <Price title="Per Campaign" amount="₹499" suffix="one-time" highlight bullets={["Unlimited plays", "QR + share link", "Full analytics"]} />
            <Price title="Retargeting" amount="₹0.15" suffix="/ email" bullets={["₹1.75 / WhatsApp msg", "Verified leads only", "Delivery tracking"]} />
          </div>
          <p className="mt-3 text-xs text-[#0A0A0A]/50">All pricing excludes 18% GST. WhatsApp retargeting launching soon.</p>
        </div>
      </section>

      {/* ─────────────── FINAL CTA ─────────────── */}
      <section className="bg-[#F5A800]">
        <div className="max-w-4xl mx-auto px-4 md:px-6 py-16 md:py-20 text-center">
          <h2
            className="text-4xl md:text-5xl uppercase leading-[0.9]"
            style={{ fontFamily: ANTON }}
          >
            Ready to launch your first campaign?
          </h2>
          <p className="mt-4 text-[#0A0A0A]/80 max-w-xl mx-auto">
            15 days free. No card. Cancel anytime. Set up in five minutes.
          </p>
          <div className="mt-8">
            <Link to="/vendor/register" data-testid="fb-final-cta">
              <Button
                className="h-14 px-8 text-base font-black uppercase tracking-widest bg-[#0A0A0A] text-[#F5A800] border-2 border-[#0A0A0A] hover:bg-white hover:text-[#0A0A0A]"
                style={{ fontFamily: ANTON }}
              >
                Get Started Free <ArrowRight className="h-5 w-5 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#0A0A0A] text-white/70 border-t-2 border-[#F5A800]">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-8 flex flex-col md:flex-row md:items-center md:justify-between gap-4 text-xs">
          <BrandMark onDark size="footer" />
          <div className="flex items-center gap-5 font-bold uppercase tracking-widest">
            <Link to="/" className="hover:text-[#F5A800]">Home</Link>
            <Link to="/explore" className="hover:text-[#F5A800]">Explore</Link>
            <Link to="/contact" className="hover:text-[#F5A800]">Contact</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}

/* ─── Sub-components ─── */

function StatTile({ label, value }) {
  return (
    <div className="bg-[#F7F4EC] border-2 border-[#0A0A0A] rounded-xl p-3">
      <p className="text-2xl leading-none" style={{ fontFamily: ANTON }}>{value}</p>
      <p className="text-[10px] uppercase tracking-widest text-[#0A0A0A]/60 mt-1">{label}</p>
    </div>
  );
}

function GameTypeCard({ icon, label, tagline, desc, testid }) {
  return (
    <div
      className="rounded-2xl border-2 border-[#0A0A0A] bg-white p-5 md:p-6 flex flex-col gap-3"
      style={{ boxShadow: "5px 5px 0 #0A0A0A" }}
      data-testid={testid}
    >
      <div className="flex items-center gap-3">
        <div className="h-10 w-10 rounded-lg bg-[#F5A800] text-[#0A0A0A] flex items-center justify-center border-2 border-[#0A0A0A]">
          {icon}
        </div>
        <p className="text-2xl uppercase leading-none" style={{ fontFamily: ANTON }}>{label}</p>
      </div>
      <p className="text-sm font-semibold text-[#0A0A0A]/80">{tagline}</p>
      <p className="text-sm text-[#0A0A0A]/70 leading-relaxed">{desc}</p>
    </div>
  );
}

function FeatureLine({ children }) {
  return (
    <li className="flex items-start gap-2 leading-snug">
      <ShieldCheck className="h-4 w-4 text-[#F5A800] mt-0.5 shrink-0" />
      <span>{children}</span>
    </li>
  );
}

function Step({ step, icon, title, desc }) {
  return (
    <div className="rounded-2xl border-2 border-white/25 bg-white/5 p-5">
      <div className="flex items-center gap-3">
        <div className="h-10 w-10 rounded-lg bg-[#F5A800] text-[#0A0A0A] flex items-center justify-center border-2 border-[#F5A800]">{icon}</div>
        <span className="text-3xl leading-none text-[#F5A800]" style={{ fontFamily: ANTON }}>{step}</span>
      </div>
      <h3 className="mt-4 text-xl uppercase leading-tight" style={{ fontFamily: ANTON }}>{title}</h3>
      <p className="mt-1.5 text-sm text-white/70 leading-relaxed">{desc}</p>
    </div>
  );
}

function Price({ title, amount, suffix, bullets = [], highlight = false }) {
  return (
    <div className={`rounded-2xl border-2 p-5 ${highlight ? "bg-[#F5A800] text-[#0A0A0A] border-[#F5A800]" : "bg-white text-[#0A0A0A] border-[#0A0A0A]"}`} style={{ boxShadow: highlight ? "6px 6px 0 #0A0A0A" : "4px 4px 0 #0A0A0A" }}>
      <p className={`text-xs font-black uppercase tracking-widest ${highlight ? "text-[#0A0A0A]/70" : "text-[#0A0A0A]/60"}`}>{title}</p>
      <p className="mt-2 text-4xl leading-none" style={{ fontFamily: ANTON }}>
        {amount}
        <span className={`ml-2 align-baseline text-xs font-bold uppercase tracking-widest ${highlight ? "text-[#0A0A0A]/60" : "text-[#0A0A0A]/50"}`}>{suffix}</span>
      </p>
      <ul className="mt-4 space-y-1.5 text-sm">
        {bullets.map((b) => (
          <li key={b} className="flex items-start gap-2">
            <span className={`mt-1.5 h-1.5 w-1.5 rounded-full shrink-0 ${highlight ? "bg-[#0A0A0A]" : "bg-[#F5A800]"}`} />
            <span className={highlight ? "text-[#0A0A0A]/85" : "text-[#0A0A0A]/80"}>{b}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
